import React, { useEffect, useMemo, useState } from "react";
import { getProducts } from "../../services/shop";
import { UiProduct } from "../../types/Product";
import { BASE_FOLDER, WOO_URL } from "../../config";

type SortKey = "name" | "category" | "price" | "stock" | "totalSold";
type SortDir = "asc" | "desc";

function formatUSD(v: number) {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export default function Products() {
  // ===== NEW: real data state =====
  const [products, setProducts] = useState<UiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ===== Filters state (unchanged) =====
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [stockFilter, setStockFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ===== NEW: fetch once =====
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getProducts("summary");
        if (!alive) return;
        setProducts(data);
        setError(null);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load products");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category))).sort(),
    [products]
  );

  // ===== Derived with filters (unchanged) =====
  const filtered = useMemo(() => {
    return products
      .map((p) => ({ ...p }))
      .filter((p) => {
        if (q) {
          const s = q.toLowerCase();
          if (
            !(
              p.name.toLowerCase().includes(s) ||
              p.category.toLowerCase().includes(s)
            )
          )
            return false;
        }
        if (category !== "all" && p.category !== category) return false;
        if (stockFilter !== "all") {
          if (stockFilter === "in" && p.stock <= 0) return false;
          if (stockFilter === "low" && !(p.stock > 0 && p.stock <= 10))
            return false;
          if (stockFilter === "out" && p.stock !== 0) return false;
        }
        return true;
      });
  }, [products, q, category, stockFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a: any, b: any) => {
      let va: any = a[sortKey];
      let vb: any = b[sortKey];
      if (typeof va === "string" && typeof vb === "string") {
        return va.localeCompare(vb) * dir;
      }
      return (va - vb) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function resetFilters() {
    setQ("");
    setCategory("all");
    setStockFilter("all");
    setSortKey("name");
    setSortDir("asc");
    setPage(1);
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">Products</h2>
        <div className="right">
          <a
            href="/management/account/products/create"
            className="btn btn-primary"
          >
            <i className="fa-solid fa-plus"></i> New Product
          </a>
        </div>
      </div>

      <div className="section-card">
        {/* Status */}
        {loading && (
          <div className="card mb-3">
            <div className="card-body">Loading products…</div>
          </div>
        )}
        {error && (
          <div className="card mb-3">
            <div className="card-body text-danger">Error: {error}</div>
          </div>
        )}

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label mb-1">Search</label>
                <input
                  className="form-control"
                  placeholder="Search name or category…"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label mb-1">Category</label>
                <select
                  className="form-select"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label mb-1">Stock</label>
                <select
                  className="form-select"
                  value={stockFilter}
                  onChange={(e) => {
                    setStockFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value="in">In stock</option>
                  <option value="low">Low (≤10)</option>
                  <option value="out">Out of stock</option>
                </select>
              </div>
              <div className="col-6 col-md-2 d-flex gap-2">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table (unchanged) */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">
                Showing <strong>{pageItems.length}</strong> of{" "}
                <strong>{total}</strong> products
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">Rows:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 80 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <Th
                      onClick={() => toggleSort("name")}
                      label="Product"
                      active={sortKey === "name"}
                      dir={sortDir}
                    />
                    <Th
                      onClick={() => toggleSort("category")}
                      label="Category"
                      active={sortKey === "category"}
                      dir={sortDir}
                    />
                    <Th
                      onClick={() => toggleSort("price")}
                      label="Price"
                      active={sortKey === "price"}
                      dir={sortDir}
                      className="text-end"
                    />
                    <Th
                      onClick={() => toggleSort("stock")}
                      label="Stock"
                      active={sortKey === "stock"}
                      dir={sortDir}
                      className="text-end"
                    />
                    <Th
                      onClick={() => toggleSort("totalSold")}
                      label="Sold"
                      active={sortKey === "totalSold"}
                      dir={sortDir}
                      className="text-end"
                    />
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((p) => {
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="fw-semibold">{p.name}</div>
                        </td>
                        <td>{p.category}</td>
                        <td className="text-end">{formatUSD(p.price)}</td>
                        <td className="text-end">
                          {p.stock === 0 ? (
                            <span className="ms-auto badge bg-danger">Out</span>
                          ) : p.stock <= 10 ? (
                            <span className="ms-auto badge bg-warning text-dark">
                              {p.stock}
                            </span>
                          ) : (
                            <span className="ms-auto badge bg-success">
                              {p.stock}
                            </span>
                          )}
                        </td>
                        <td className="text-end">{p.totalSold}</td>
                        <td className="text-end">
                          <div className="btn-group">
                            <a
                              className="btn btn-sm btn-outline-secondary"
                              target="_blank"
                              href={WOO_URL + `/products/${p.slug}`}
                              rel="noreferrer"
                            >
                              View
                            </a>
                            <a
                              className="btn btn-sm btn-outline-primary"
                              href={
                                "/" +
                                BASE_FOLDER +
                                `/account/products/${p.id}/edit`
                              }
                            >
                              Edit
                            </a>
                            {/* <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                alert(`Delete product ${p.id} - ${p.name}`)
                              }
                            >
                              Delete
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {pageItems.length === 0 && !loading && !error && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No products match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Page {page} / {totalPages}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({
  label,
  onClick,
  active,
  dir,
  className = "",
}: {
  label: string;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
  className?: string;
}) {
  return (
    <th
      role="button"
      onClick={onClick}
      className={`user-select-none ${className}`}
      title="Sort"
    >
      {label}{" "}
      {active ? (
        <i
          className={`ms-1 fa-solid fa-sort-${dir === "asc" ? "up" : "down"}`}
        />
      ) : (
        <i className="ms-1 fa-solid fa-sort text-muted" />
      )}
    </th>
  );
}
